import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, Subscription, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GridReadyEvent,
  ColDef,
  IDatasource,
  GridOptions,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  ComparatorMap,
  ReusableStreamProperty,
  stringComparer,
  numberComparer,
  roleComparer
} from '@ipreo/muni-shared';
import { NegativeCasesService } from '../../../../gameday/services/negative-cases.service';
import { ManagerOverviewReport } from '../../../models/manager';
import { ManagersOverviewService } from '../../../services/managers-overview.service';

@Component({
  providers: [ManagersOverviewService],
  selector: 'muni-gameday-managers-overview',
  templateUrl: './managers-overview.template.html',
  styleUrls: ['./managers-overview.styles.scss'],
  standalone: false
})
export class ManagersOverviewComponent implements OnDestroy {
  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  public readonly columnDefs: ColDef[] = [
    {
      headerName: 'Manager',
      field: 'name',
      comparator: stringComparer,
      suppressHeaderMenuButton: true
    },
    {
      headerName: 'Retail',
      field: 'retailAmount',
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Institutional',
      field: 'institutionalAmount',
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Stock',
      field: 'stockAmount',
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Total',
      field: 'total',
      sort: 'desc',
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Manager Type',
      field: 'role',
      type: 'numericColumn', // to get right align
      comparator: roleComparer,
      suppressHeaderMenuButton: true
    }
  ];

  public readonly gridOptions: GridOptions = {
    onGridReady: (params: GridReadyEvent) => {
      this.gridReadySubj.next(params);
    },
    autoSizeStrategy: { type: 'fitGridWidth' },
    onGridSizeChanged: ({ api }) => api.sizeColumnsToFit()
  };

  @ReusableStreamProperty
  public readonly emptyMessage: Observable<string> =
    this.negativeCasesService.messages.pipe(
      map((messages) => messages.managers)
    );

  public readonly source: Observable<ManagerOverviewReport[]> =
    this.managersOverviewService.getManagersOverviewData();

  public readonly totals: Observable<ManagerOverviewReport> =
    this.managersOverviewService.getManagersOverviewTotalsByTypes().pipe(
      map((report: ManagerOverviewReport) => ({
        ...report,
        name: 'Total'
      }))
    );

  @ReusableStreamProperty
  private readonly grid$: Observable<
    [GridReadyEvent, ManagerOverviewReport[], ManagerOverviewReport]
  > = combineLatest(this.gridReadySubj, this.source, this.totals);

  private readonly gridSubscribtion: Subscription = this.grid$.subscribe(
    ([{ api }, managerOverview, totals]) => {
      const sortMap: ComparatorMap[] = this.columnDefs
        .map(({ field, comparator }: any) => ({
          colId: field,
          comparator: comparator
            ? (a: any, b: any) => (comparator as any)(a, b)
            : undefined
        }))
        .filter((item) => item.comparator);
      const datasource: IDatasource =
        this.infiniteScrollingService.getDataSource(
          managerOverview,
          undefined,
          sortMap
        );
      api.setDatasource(datasource);
      api.setGridOption('pinnedTopRowData', [totals]);
    }
  );

  constructor(
    private managersOverviewService: ManagersOverviewService,
    private negativeCasesService: NegativeCasesService,
    private infiniteScrollingService: InfiniteScrollingService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public ngOnDestroy(): void {
    this.gridSubscribtion.unsubscribe();
  }
}
